<app-modal>
  <div class="if-modal-title">
    <h3 class="bdf-modal-title">
      BDF generation
      <span class="bdf-modal-title__project">{{ project()?.name ?? '' }}</span>
    </h3>
  </div>

  <div class="if-modal-content">
    @if (project() && !project()!.isMaster) {
      <ul class="bdf-child-list" role="list">
        <li class="bdf-row bdf-row--child">
          <div class="bdf-row__main">
            <div class="bdf-row__top">
              <div class="bdf-row__name">BDF mesh</div>
              <div class="bdf-row__status" role="status" aria-live="polite" aria-atomic="true">
                <app-bdf-row-pill
                  size="xxs"
                  [status]="singleStatus()?.status ?? 'idle'"
                  [showAll]="true">
                </app-bdf-row-pill>
                @if (singleStatus()?.highRes) {
                  <app-bdf-highres-badge></app-bdf-highres-badge>
                }
                @if (singleStatus()?.status === 'error' && singleStatus()?.error) {
                  <span class="bdf-row__error">{{ singleStatus()!.error }}</span>
                }
              </div>
            </div>
          </div>
          <div class="bdf-row__actions">
            @if (singleStatus()?.status === 'queued' || singleStatus()?.status === 'running') {
              <app-button
                variant="ghost"
                size="xs"
                ghostType="error"
                [disabled]="isActionBusy('cancel:' + project()!.id)"
                (click)="onCancelSingle()">
                Stop
              </app-button>
            } @else if (singleStatus()?.status === 'done') {
              <app-button
                variant="outline"
                size="xs"
                [disabled]="isActionBusy('download:' + project()!.id)"
                (click)="onDownloadSingle()">
                Download
              </app-button>
              <app-button
                variant="ghost"
                size="xs"
                [disabled]="isActionBusy('generate:' + project()!.id)"
                [attr.aria-label]="'Regenerate BDF'"
                (click)="onRegenerateSingle()">
                Regenerate
              </app-button>
            } @else {
              <app-button
                variant="outline"
                size="xs"
                [disabled]="generateDisabled()"
                (click)="onGenerate()">
                Generate
              </app-button>
            }
          </div>
        </li>
      </ul>
    } @else {
      <p class="bdf-master-hint" id="bdf-master-hint">
        Select one or more manufacturing cases to generate BDF files.
      </p>
      <ul class="bdf-child-list" role="list" aria-describedby="bdf-master-hint">
        @for (child of children(); track child.id) {
          @let childJob = getChildStatus(child.id)();
          <li class="bdf-row bdf-row--child">
            <div class="bdf-row__main">
              <div class="bdf-row__top">
                <app-checkbox
                  class="bdf-row__check"
                  [label]="child.name"
                  [ariaLabel]="child.methodLabel"
                  [ngModel]="isChildSelected(child.id)"
                  (ngModelChange)="toggleChild(child.id, $event)">
                </app-checkbox>
                <div class="bdf-row__status" role="status" aria-live="polite" aria-atomic="true">
                  <app-bdf-row-pill
                    size="xxs"
                    [status]="childJob?.status ?? 'idle'"
                    [showAll]="true">
                  </app-bdf-row-pill>
                  @if (childJob?.highRes) {
                    <app-bdf-highres-badge></app-bdf-highres-badge>
                  }
                </div>
              </div>
              @if (child.detail) {
                <div class="bdf-row__detail">{{ child.detail }}</div>
              }
            </div>
            <div class="bdf-row__actions">
              @if (childJob?.status === 'queued' || childJob?.status === 'running') {
                <app-button
                  variant="ghost"
                  size="xs"
                  ghostType="error"
                  [disabled]="isActionBusy('cancel:' + child.id)"
                  [attr.aria-label]="'Stop BDF generation for ' + child.methodLabel"
                  (click)="onCancelChild(child.id)">
                  Stop
                </app-button>
              } @else if (childJob?.status === 'done') {
                <app-button
                  variant="outline"
                  size="xs"
                  [disabled]="isActionBusy('download:' + child.id)"
                  [attr.aria-label]="'Download BDF for ' + child.methodLabel"
                  (click)="onDownloadChild(child.id)">
                  Download
                </app-button>
                <app-button
                  variant="ghost"
                  size="xs"
                  [disabled]="isActionBusy('generate:' + (project()?.id ?? child.id))"
                  [attr.aria-label]="'Regenerate BDF for ' + child.methodLabel"
                  (click)="onRegenerateChild(child.id)">
                  Regenerate
                </app-button>
              }
            </div>
          </li>
        }
      </ul>
    }

    <div class="bdf-options">
      <app-checkbox
        class="bdf-high-res-toggle"
        label="High resolution mesh (slower)"
        [ngModel]="highRes()"
        (ngModelChange)="toggleHighRes($event)">
      </app-checkbox>
    </div>
  </div>

  <div class="if-modal-actions">
    <app-button variant="ghost" size="sm" (click)="onClose()">Cancel</app-button>
    @if (project()?.isMaster) {
      <app-button
        variant="outline"
        size="sm"
        [disabled]="generateDisabled()"
        (click)="onGenerate()">
        Generate selected
      </app-button>
      <app-button
        class="bdf-bundle-button"
        variant="primary"
        size="sm"
        [disabled]="!bundleReady() || bundleBusy() || isActionBusy('bundle:' + project()!.id)"
        [attr.aria-busy]="bundleBusy()"
        (click)="onDownloadBundle()">
        <!-- Same label either way; only the leading icon swaps, so the button keeps its width. -->
        @if (bundleBusy()) {
          <app-inline-spinner class="bdf-bundle-icon" aria-hidden="true"></app-inline-spinner>
        } @else {
          <svg class="bdf-bundle-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        }
        Download selected as ZIP
      </app-button>
    }
  </div>
</app-modal>
